import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Typography, Box, Button, FormControl, FormLabel,
  Textarea, IconButton, Divider, Alert, Chip,
  Tooltip, Stack, Avatar,
} from "@mui/joy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { agreeEmail, disagreeEmail } from "../emailTempates";
import avatar from "../../../Male_Avatar.jpg";

const ContactPage = ({ representative, setOpen, bill, stance, address }) => {
  const [emailText, setEmailText] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const template =
      stance === "oppose"
        ? disagreeEmail(representative, bill, address)
        : agreeEmail(representative, bill, address);
    setEmailText(template);
  }, [representative, bill, stance, address]);

  const handleCopy = () => {
    navigator.clipboard.writeText(emailText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const goToRepWebsite = () => {
    window.open(representative.officialWebsiteUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#171a1c", mb: "6px" }}>
        Your Message is Ready
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#535862", lineHeight: 1.6, mb: "20px" }}>
        We&apos;ve drafted a message for{" "}
        <strong style={{ color: "#171a1c" }}>{representative.fullName}</strong>.
        Edit it below, then visit their website to send it.
      </Typography>

      <Alert
        variant="soft"
        color="primary"
        size="sm"
        sx={{ mb: "20px", fontSize: "13px" }}
      >
        Personal stories make the biggest impact. Add your own experience to
        make your message stronger.
      </Alert>

      {/* Rep info */}
      <Stack direction="row" spacing="12px" alignItems="center" sx={{ mb: "16px" }}>
        <Avatar
          src={representative.imageUrl || avatar}
          alt={representative.fullName || "Representative"}
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        />
        <Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#171a1c" }}>
            {representative.fullName}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#535862" }}>
            {[representative.party, representative.state].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: "20px" }} />

      {/* Email editor */}
      <FormControl>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px" }}>
          <FormLabel sx={{ fontSize: "14px", fontWeight: 600, color: "#374151", mb: 0 }}>
            Your message
          </FormLabel>
          <Chip size="sm" variant="soft" color="success" sx={{ fontSize: "11px" }}>
            Ready to send
          </Chip>
        </Box>
        <Textarea
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
          minRows={8}
          maxRows={14}
          placeholder="Edit your message..."
          endDecorator={
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pt: "var(--Textarea-paddingBlock)",
                borderTop: "1px solid",
                borderColor: "divider",
                flex: "auto",
              }}
            >
              <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} variant="solid">
                <IconButton
                  variant="plain"
                  color={copied ? "success" : "neutral"}
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          }
          sx={{
            fontSize: "14px",
            lineHeight: 1.6,
            "--Textarea-focusedHighlight": "#0075b2",
            "&:focus-within": {
              borderColor: "#0075b2",
              boxShadow: "0 0 0 3px rgba(0,117,178,0.15)",
            },
          }}
        />
      </FormControl>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: "24px" }}>
        <Button
          fullWidth
          size="lg"
          endDecorator={<OpenInNewIcon />}
          onClick={goToRepWebsite}
          sx={{
            backgroundColor: "#003049",
            color: "white",
            fontWeight: 600,
            fontSize: "16px",
            borderRadius: "6px",
            minHeight: "48px",
            "&:hover": { backgroundColor: "#00243a" },
          }}
        >
          Contact my representative
        </Button>
        <Button
          fullWidth
          variant="plain"
          color="neutral"
          onClick={() => setOpen(false)}
          sx={{ fontSize: "15px", fontWeight: 500 }}
        >
          Close
        </Button>
      </Box>
    </>
  );
};

ContactPage.propTypes = {
  setOpen: PropTypes.func.isRequired,
  stance: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  representative: PropTypes.object.isRequired,
  bill: PropTypes.object.isRequired,
};

export default ContactPage;
